import { PropsWithChildren } from 'react';
import { IntlProvider, ReactIntlErrorCode } from 'react-intl';
import { useConfig } from './configContext';

const handleFormatError = (error: Error) => {
    // probably not a good idea to throw errors for every formatting issue, so we are targeting only specific ones, based on message ID
    const messageIDRegex = /MessageID:\s*(\w+)/;
    const messageId = messageIDRegex.exec(error.message)?.[1];

    if (!messageId) {
        console.warn(`Intl formatting error without message ID. Message ID not found in error message: ${error}`);
        return;
    }

    // if error is format error, and we know message ID, check the ID and possibly rethrow it
    // after rethrowing, it has to be catched later by the component
    // this is needed because of not working string interpolation in FormattedMessage viz. https://jira.mediafactory.cz/browse/SRSERP-244
    // tldr: on some sites, there is a situation where format JS interpolation is not working due to collision with other scripts like MicrosoftAjax.js
    // the workaround is to catch the error in the component and then use custom fallback solution
    if (
        messageId === 'search_overview' ||
        messageId === 'search_overview_mobile' ||
        messageId === 'results_count' ||
        messageId === 'search_results_overview'
    ) {
        throw new Error(error.message);
    }
};

/**
 * IntlContextProvider is a wrapper for react-intl IntlProvider.
 *
 * @remarks
 * It is used to provide translations to the app.
 * Uses custom error handler for react-intl errors, errors are rethrown and catched in components.
 * A warning is displayed instead of an error, if error is thrown because of missing message ID.
 *
 */
export const IntlContextProvider: React.FC<PropsWithChildren> = ({ children }) => {
    const config = useConfig();

    return (
        <IntlProvider
            locale="cs"
            messages={config.translations}
            onError={(error) => {
                if (error.code !== ReactIntlErrorCode.FORMAT_ERROR) {
                    console.warn(error);
                    return;
                }

                handleFormatError(error);
            }}
        >
            {children}
        </IntlProvider>
    );
};
